/**
 * The application shell: navigation, the environment badge, and the sample-data badge.
 *
 * Two rules are enforced here rather than left to each page:
 *
 *  - Every screen carries the environment badge (CLAUDE.md 3.4). It lives in the shell header,
 *    so a new page cannot forget it.
 *  - Navigation shows the full long-term structure with unbuilt items disabled and labelled
 *    "Phase N" (ARCHITECTURE.md 11). That is honest about what exists, and it makes the roadmap
 *    visible in the product rather than only in a document.
 */

import { NavLink, useLocation } from 'react-router-dom';
import { Fragment, type ReactNode } from 'react';
import type { EnvironmentBadge, User } from '../api.js';

export interface NavItem {
  label: string;
  to?: string;
  phase: number;
  /**
   * Deferred to a later version (ROADMAP.md, 2026-09-17): listed so the long-term shape stays
   * visible, labelled so nobody waits for it.
   */
  deferred?: boolean;
  /**
   * A section caption. Consecutive items sharing a `group` sit under one caption, rendered where
   * the group starts. Finance is the first section (FINANCE.md 8.2: "its own major section").
   */
  group?: string;
}

/**
 * ARCHITECTURE.md section 11. Phase 1 built Properties, Activity, and Settings; Phase 2 adds the
 * Agent Control Center and the Approval Center; Phase 3 opens the Finance section with
 * Transactions, Bookkeeping, and Reconciliation. Everything still unbuilt stays listed and
 * disabled, labelled with the phase it arrives in.
 */
export const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', phase: 2 },
  { label: 'Properties', to: '/properties', phase: 1 },
  { label: 'Guesty listings', to: '/guesty-listings', phase: 9 },
  { label: 'Reservations', to: '/reservations', phase: 4 },
  { label: 'Guests', to: '/guests', phase: 8 },
  { label: 'Autonomy', to: '/autonomy', phase: 12 },
  { label: 'Operations', to: '/operations', phase: 7 },
  { label: 'Maintenance', to: '/maintenance', phase: 7 },
  { label: 'Revenue', to: '/revenue', phase: 4 },
  { label: 'Overview', to: '/finance', phase: 5, group: 'Finance' },
  { label: 'By property', to: '/finance/properties', phase: 5, group: 'Finance' },
  { label: 'Transactions', to: '/finance/transactions', phase: 3, group: 'Finance' },
  { label: 'Bookkeeping', to: '/finance/bookkeeping', phase: 3, group: 'Finance' },
  { label: 'Reconciliation', to: '/finance/reconciliation', phase: 3, group: 'Finance' },
  { label: 'Chart mapping', to: '/finance/chart', phase: 5, group: 'Finance' },
  { label: 'QuickBooks classes', to: '/finance/classes', phase: 5, group: 'Finance' },
  { label: 'Monthly Review', to: '/finance/monthly-review', phase: 5, group: 'Finance' },
  { label: 'Cash Flow', to: '/finance/cash', phase: 5, group: 'Finance' },
  { label: 'Financial Statements', to: '/finance/statements', phase: 5, group: 'Finance' },
  { label: 'Budgets', to: '/finance/budgets', phase: 5, group: 'Finance' },
  { label: 'Debt', to: '/finance/debt', phase: 5, group: 'Finance' },
  { label: 'CapEx', to: '/finance/capex', phase: 5, group: 'Finance' },
  { label: 'Reports', to: '/finance/reports', phase: 5, group: 'Finance' },
  { label: 'Acquisitions', phase: 10, deferred: true },
  { label: 'Marketing', phase: 11, deferred: true },
  { label: 'Agents', to: '/agents', phase: 2 },
  { label: 'Ask', to: '/ask', phase: 6 },
  { label: 'Knowledge', to: '/knowledge', phase: 6 },
  { label: 'Approvals', to: '/approvals', phase: 2 },
  { label: 'Activity', to: '/activity', phase: 1 },
  { label: 'Development Roadmap', to: '/roadmap', phase: 1 },
  { label: 'Settings', to: '/settings', phase: 1 },
];

export function EnvironmentBadges({ badge }: { badge: EnvironmentBadge | null }): ReactNode {
  if (!badge) return null;

  const tone =
    badge.environment === 'production'
      ? { background: '#7f1d1d', color: '#fff' }
      : { background: '#1e3a5f', color: '#fff' };

  return (
    <div className="badges">
      <span className="badge" style={tone} title="The environment this screen is reading from">
        {badge.label}
      </span>
      {badge.sampleDataLabel && (
        <span
          className="badge badge-sample"
          title="This screen is fed by seeded or mock data. Nothing here is real."
        >
          {badge.sampleDataLabel}
        </span>
      )}
    </div>
  );
}

export interface ShellProps {
  badge: EnvironmentBadge | null;
  user: User | null;
  onSignOut: () => void;
  readOnly: boolean;
  children: ReactNode;
}

export function Shell({ badge, user, onSignOut, readOnly, children }: ShellProps): ReactNode {
  const location = useLocation();

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark">SIXGEN</span>
          <span className="brand-sub">AI Operating System</span>
        </div>
        <nav>
          {NAV_ITEMS.map((item, index) => (
            <Fragment key={item.label}>
              {item.group && item.group !== NAV_ITEMS[index - 1]?.group && (
                <span className="nav-group">{item.group}</span>
              )}
              {item.to ? (
                <NavLink
                  to={item.to}
                  className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                >
                  {item.label}
                </NavLink>
              ) : (
                <span
                  className="nav-item disabled"
                  title={
                    item.deferred ? 'Deferred to a later version' : `Arrives in Phase ${item.phase}`
                  }
                >
                  {item.label}
                  <span className="phase-tag">
                    {item.deferred ? 'Later' : `Phase ${item.phase}`}
                  </span>
                </span>
              )}
            </Fragment>
          ))}
        </nav>
      </aside>

      <div className="main">
        <header className="topbar">
          <EnvironmentBadges badge={badge} />
          {readOnly && (
            <span className="badge badge-readonly" title="The kill switch is engaged">
              READ-ONLY — writes are blocked
            </span>
          )}
          <div className="spacer" />
          {user && (
            <div className="user">
              <span className="user-email">{user.email}</span>
              <button type="button" className="link" onClick={onSignOut}>
                Sign out
              </button>
            </div>
          )}
        </header>
        <main key={location.pathname} className="content">
          {children}
        </main>
      </div>
    </div>
  );
}
